
import { Award, Leaf, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";
import { cn } from "@/lib/utils";

const dimensions = [
  {
    label: "Width (W)",
    value: "3.61 ft",
  },
  {
    label: "Depth (D)",
    value: "3.87 ft",
  },
  {
    label: "Height (H)",
    value: "7.22 ft",
  },
  {
    label: "Door (Swing Clearance)",
    value: "3.61 ft",
  },
];

const certifications = [
  {
    name: "ISO 9001 Certified for Exceptional Quality Standards",
    icon: <Award className="w-4 h-4 text-primary" />,
    category: "Quality",
  },
  {
    name: "ISO 14001 Certified for Sustainable Environmental Practices",
    icon: <Leaf className="w-4 h-4 text-primary" />,
    category: "Environmental",
  },
  {
    name: "ISO 45001 Certified for Safety and Wellbeing",
    icon: <ShieldCheck className="w-4 h-4 text-primary" />,
    category: "Safety",
  },
  {
    name: "GREENGUARD Certified for Healthier Indoor Air",
    icon: <Leaf className="w-4 h-4 text-primary" />,
    category: "Environmental",
  },
  {
    name: "FSC Certified Wood for Sustainable Forest Management",
    icon: <Leaf className="w-4 h-4 text-primary" />,
    category: "Environmental",
  },
  {
    name: "Safety Glass Certified to AS/NZS 2208 Standards",
    icon: <ShieldCheck className="w-4 h-4 text-primary" />,
    category: "Safety",
  },
  {
    name: "Acoustic Performance Certified (AS 5335 / ISO 23351-1 Tested)",
    icon: <Award className="w-4 h-4 text-primary" />,
    category: "Quality",
  },
];

const CertificationGroup = ({ title, certs }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full rounded-md border p-2 mb-2">
      <CollapsibleTrigger className="w-full flex items-center justify-between p-1 font-medium">
        <span>{title}</span>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-2">
          {certs.map((cert, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-secondary/30 p-2 rounded-md">
              <div className="bg-secondary/70 p-1 rounded-full flex-shrink-0">
                {cert.icon}
              </div>
              <div className="text-sm">{cert.name}</div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const BoothSpecsSection = () => {
  // Group certifications by category
  const qualityCerts = certifications.filter(cert => cert.category === "Quality");
  const environmentalCerts = certifications.filter(cert => cert.category === "Environmental");
  const safetyCerts = certifications.filter(cert => cert.category === "Safety");

  return (
    <section id="booth-specs" className="py-10 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="font-montserrat text-3xl md:text-4xl font-extrabold text-foreground mb-2">
            Booth Specifications & Certifications
          </h2>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            Custom design to match any aesthetic, made with premium materials, backed by international certifications.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center mb-10">
          {/* Specifications Card */}
          <div className="order-2 lg:order-1">
            <Card className="border border-muted/20 shadow-sm">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-lg font-bold text-foreground">
                  Technical Specifications
                </CardTitle>
              </CardHeader>
              <CardContent className="py-3 px-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold">Capacity:</h3>
                  <p className="text-base text-muted-foreground">1 Person</p>
                </div>
                
                <div>
                  <h3 className="text-base font-semibold mb-2">Dimensions:</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {dimensions.map((dimension, index) => (
                      <div
                        key={index}
                        className={cn(
                          "bg-secondary/30 p-2 rounded-lg flex justify-between",
                          index % 2 === 0 ? "mr-1" : "ml-1"
                        )}
                      >
                        <p className="text-sm text-muted-foreground">{dimension.label}:</p>
                        <p className="text-sm font-medium text-primary">{dimension.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2 relative">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/lovable-uploads/cf3ccfea-89f4-4e89-8af8-8a6528fe8a7e.png" 
                alt="Quiet Cove Booth - Premium Design and Construction" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Certifications Section - Using Collapsible Groups */}
        <div className="mb-6">
          <h3 className="font-montserrat text-xl font-bold text-foreground mb-4 text-center">
            Certifications & Standards
          </h3>
          
          <div className="space-y-2">
            <CertificationGroup title="Quality Certifications" certs={qualityCerts} />
            <CertificationGroup title="Environmental Certifications" certs={environmentalCerts} />
            <CertificationGroup title="Safety Certifications" certs={safetyCerts} />
          </div>
        </div>
        
        <div className="relative">
          <Separator className="w-full h-px bg-quietcove-sand/30 my-2" />
        </div>
      </div>
    </section>
  );
};

export default BoothSpecsSection;
